import * as React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Info, DollarSign, User, Briefcase, Scale, ShieldCheck, Settings, CreditCard, Bug, Gift, HelpCircle, Building2 } from "lucide-react";
import { BugReportDialog } from "@/components/support/BugReportDialog";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useOptimizedUserRole } from "@/hooks/useOptimizedUserRole";
import UsageMenuItem from "@/components/UsageMenuItem";
import FreeQuotaBanner from "@/components/attorney/FreeQuotaBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Eyebrow } from "@/components/ui/editorial";

export const FloatingMenuButton = () => {
  const { user, signOut } = useAuth();
  const { isAdmin, isAttorney, isInHouseCounsel, hasClientAccess } = useOptimizedUserRole();
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [bugReportOpen, setBugReportOpen] = React.useState(false);
  const navigate = useNavigate();

  return (
    <div className="fixed top-1.5 right-1.5 sm:top-3 sm:right-3 lg:top-4 lg:right-4 z-50">
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="outline" 
            size="icon" 
            className="relative shadow-lg bg-background/80 backdrop-blur-sm h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 rounded-none border-foreground/30"
            aria-label="Open menu"
            >
              <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
              {user && (
                <div className="absolute -top-1 -right-1 flex items-center justify-center h-3.5 w-3.5 sm:h-4 sm:w-4 bg-muted backdrop-blur-sm rounded-full border-2 border-background">
                  <ShieldCheck className="h-2 w-2 sm:h-2.5 sm:w-2.5 text-primary" />
                </div>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-80 bg-background border-l border-border rounded-none flex flex-col p-0">
          {/* Header eyebrow */}
          <div className="px-6 pt-8 pb-4 border-b border-border">
            <Eyebrow withBar size="sm" tone="foreground">
              {isAdmin ? 'Administrator' : isAttorney ? 'Attorney' : isInHouseCounsel ? 'In-House Counsel' : user ? 'Client' : 'Welcome'}
            </Eyebrow>
            <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Menu</p>
          </div>

          <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
            {/* Usage Widget - Only show for clients */}
            {hasClientAccess() && !isAttorney && !isAdmin && !isInHouseCounsel && (
              <div className="px-6 pt-5 pb-4 border-b border-border bg-muted/20">
                <UsageMenuItem onItemClick={() => setIsSheetOpen(false)} />
              </div>
            )}

            {/* Free Plan Quota - Attorneys on Free tier (renders nothing on paid tiers) */}
            {user && isAttorney && (
              <FreeQuotaBanner onItemClick={() => setIsSheetOpen(false)} />
            )}

            {/* Navigate Section */}
            <div className="px-6 pt-5 pb-2">
              <Eyebrow size="sm">Navigate</Eyebrow>
            </div>
            <div className="flex flex-col">
              <Link
                to="/about"
                className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                onClick={() => setIsSheetOpen(false)}
              >
                <span className="flex items-center gap-3"><Info className="h-3.5 w-3.5" />About Us</span>
                <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
              </Link>

              {!isAdmin && !isAttorney && !isInHouseCounsel && (
                <Link
                  to="/signup-plan-selection"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><DollarSign className="h-3.5 w-3.5" />Pricing</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {user && hasClientAccess() && !isAttorney && !isAdmin && !isInHouseCounsel && (
                <Link
                  to="/client-portal"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><User className="h-3.5 w-3.5" />Client Portal</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {(!user || (hasClientAccess() && !isAttorney && !isAdmin && !isInHouseCounsel)) && (
                <Link
                  to="/rewards"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><Gift className="h-3.5 w-3.5" />Rewards</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {(!user || (hasClientAccess() && !isAttorney && !isAdmin && !isInHouseCounsel)) && (
                <Link
                  to="/help"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><HelpCircle className="h-3.5 w-3.5" />Help</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {!user && (
                <Link
                  to="/client-portal"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><User className="h-3.5 w-3.5" />Client Portal</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {user && isInHouseCounsel && (
                <Link
                  to="/in-house-portal"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><Building2 className="h-3.5 w-3.5" />In-House Portal</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {!isAdmin && !isInHouseCounsel && (
                <Link
                  to="/for-attorneys"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><Briefcase className="h-3.5 w-3.5" />For Attorneys</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {user && isAttorney && (
                <Link
                  to="/attorney-portal"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><Scale className="h-3.5 w-3.5" />Attorney Portal</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {user && isAdmin && (
                <Link
                  to="/admin-dashboard"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><ShieldCheck className="h-3.5 w-3.5" />Admin Dashboard</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}

              {user && isAttorney && (
                <Link
                  to="/signup/attorney"
                  className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors md:hidden"
                  onClick={() => setIsSheetOpen(false)}
                >
                  <span className="flex items-center gap-3"><CreditCard className="h-3.5 w-3.5" />Billing &amp; Subscription</span>
                  <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                </Link>
              )}
            </div>

            {/* Account Section */}
            {user && (
              <>
                <div className="px-6 pt-6 pb-2">
                  <Eyebrow size="sm">Account</Eyebrow>
                </div>
                <div className="flex flex-col">
                  {(!isAttorney || isAdmin) && (
                    <Link
                      to="/account"
                      className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors"
                      onClick={() => setIsSheetOpen(false)}
                    >
                      <span className="flex items-center gap-3"><Settings className="h-3.5 w-3.5" />Account</span>
                      <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
                    </Link>
                  )}
                </div>
              </>
            )}

            {/* Preferences */}
            <div className="px-6 pt-6 pb-2">
              <Eyebrow size="sm">Preferences</Eyebrow>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-border">
                <span className="text-xs uppercase tracking-[0.16em] text-foreground/80">Theme</span>
                <ThemeToggle className="h-8 w-8 p-0" />
              </div>
              <button
                onClick={() => {
                  setIsSheetOpen(false);
                  setBugReportOpen(true);
                }}
                className="group flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground hover:bg-muted/40 border-t border-border transition-colors w-full text-left"
              >
                <span className="flex items-center gap-3"><Bug className="h-3.5 w-3.5" />Report a Bug</span>
                <span aria-hidden className="h-px w-3 bg-foreground/30 group-hover:bg-lexsavvy-yellow transition-colors" />
              </button>
            </div>

            {/* Sign In/Out */}
            <div className="mt-auto px-6 py-5 border-t border-border">
              {user ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    sessionStorage.removeItem('betaCodeVerified');
                    sessionStorage.removeItem('betaIntendedDestination');
                    signOut();
                    setIsSheetOpen(false);
                    navigate('/', { replace: true });
                  }}
                  className="w-full justify-center rounded-none border-foreground text-foreground text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-lexsavvy-yellow hover:text-foreground hover:border-lexsavvy-yellow"
                >
                  <User className="mr-2 h-4 w-4" />
                  Sign out
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    navigate('/auth?tab=login');
                    setIsSheetOpen(false);
                  }}
                  className="w-full justify-center rounded-none bg-foreground text-background text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-lexsavvy-yellow hover:text-foreground"
                >
                  <User className="mr-2 h-4 w-4" />
                  Sign in
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
      
      <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
    </div>
  );
};
